import React, { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/progress";
import { Button } from "./ui/button";
import { Loader2, X, Maximize2, FileText } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
interface ActiveImport {
  id: number;
  file_name: string;
  status: "PROCESSING" | "ANALYZING";
  progress: number;
  processed_rows: number;
  total_rows: number;
}
export function ImportProgressWidget() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [activeImport, setActiveImport] = useState<ActiveImport | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  // Polling para buscar importaciones activas
  useEffect(() => {
    // Si estamos en login o rutas públicas, no buscar
    if (
      pathname.startsWith("/login") ||
      pathname.startsWith("/forgot-password") ||
      pathname.startsWith("/auth")
    ) {
      setIsVisible(false);
      setIsPolling(false);
      return;
    }
    const checkActiveImport = async () => {
      try {
        const res = await fetch("/api/imports/active");
        if (res.ok) {
          const data = await res.json();
          if (data.active) {
            setActiveImport(data.file);
            setIsVisible(true);
            setIsPolling(true); // Continuar polling mientras hay importación activa
          } else {
            // Si acabamos de terminar (teníamos una activa y ahora no),
            // mostrar un mensaje de éxito brevemente
            if (activeImport) {
              setTimeout(() => {
                setIsVisible(false);
                setActiveImport(null);
              }, 3000); // Mantener visible 3 segundos al finalizar
            } else {
              setIsVisible(false);
            }
            setIsPolling(false); // Detener polling cuando no hay importaciones activas
          }
        } else {
          // Silenciosamente fallar si no auth
          if (res.status === 401 || res.status === 403) {
            setIsVisible(false);
            setIsPolling(false);
          }
        }
      } catch (error) {
      }
    };
    // Verificar inmediatamente
    checkActiveImport();
    // Solo hacer polling si hay una importación activa
    let interval = null;
    if (isPolling || activeImport) {
      interval = setInterval(checkActiveImport, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPolling, activeImport, pathname]);
  // No mostrar si no hay importación activa o si estamos en la página de importación de ese archivo
  if (!isVisible || !activeImport) return null;
  // Si estamos en la página de detalle de ESE archivo, no mostrar el widget para no duplicar
  // Asumimos ruta /importaciones?continue=ID o similar
  if (
    pathname === "/importaciones" &&
    window.location.search.includes(`continue=${activeImport.id}`)
  ) {
    return null;
  }
  const handleNavigate = () => {
    navigate(`/importaciones?continue=${activeImport.id}`);
  };
  if (isMinimized) {
    return (
      <div
        className="fixed bottom-4 right-4 z-50 bg-white shadow-lg rounded-full p-3 border border-slate-200 cursor-pointer hover:shadow-xl transition-all flex items-center gap-2 animate-in slide-in-from-bottom-10"
        onClick={() => setIsMinimized(false)}
      >
        <div className="relative">
          <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
          </span>
        </div>
        <div className="text-xs font-medium pr-2">{activeImport.progress}%</div>
      </div>
    );
  }
  return (
    <Card className="fixed bottom-4 right-4 z-50 w-80 shadow-2xl border-slate-200 animate-in slide-in-from-bottom-10">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 p-1.5 rounded-md">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h4 className="text-sm font-semibold leading-none">
                Importación en curso
              </h4>
              <p
                className="text-xs text-muted-foreground mt-1 max-w-[180px] truncate"
                title={activeImport.file_name}
              >
                {activeImport.file_name}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setIsMinimized(true)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">
                {activeImport.status === "ANALYZING"
                  ? "Analizando..."
                  : "Procesando registros..."}
              </span>
              <span className="font-medium">{activeImport.progress}%</span>
            </div>
            <Progress value={activeImport.progress} className="h-2" />
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <div className="text-xs text-muted-foreground">
              {activeImport.processed_rows} / {activeImport.total_rows} filas
            </div>
            <Button
              size="sm"
              variant="default"
              className="h-7 text-xs"
              onClick={handleNavigate}
            >
              <Maximize2 className="h-3 w-3 mr-1.5" />
              Ver Detalle
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
